import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Lock, AlertCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ResetPassword() {
    const { confirmResetPassword, error: authError } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    const oobCode = searchParams.get('oobCode');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        if (!oobCode) {
            setLocalError('Invalid or missing password reset code.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setLocalError('Passwords do not match.');
            return;
        }

        if (newPassword.length < 6) {
            setLocalError('Password should be at least 6 characters.');
            return;
        }

        setLoading(true);
        try {
            await confirmResetPassword(oobCode, newPassword);
            setSuccess(true);
        } catch (err) {
            // Error is handled in context, but we will rely on authError
        } finally {
            setLoading(false);
        }
    };

    const error = localError || authError;

    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            {/* Background decoration */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-radial from-primary-500/20 to-transparent opacity-50" />
                <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-radial from-accent-500/20 to-transparent opacity-50" />
            </div>

            <div className="w-full max-w-md relative">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-linear-to-br from-primary-500 to-accent-500 mb-4 shadow-glow">
                        <span className="text-white font-bold text-2xl">A</span>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Create New Password</h1>
                    <p className="text-gray-400">Enter your new password below</p>
                </div>

                {/* Card */}
                <div className="card">
                    {success ? (
                        <div className="text-center py-4">
                            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="text-emerald-400" size={32} />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">Password Reset Successful</h3>
                            <p className="text-gray-400 mb-6">
                                Your password has been successfully reset. You can now use your new password to log in.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                className="btn-primary w-full"
                            >
                                Continue to Login
                            </button>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
                                    <AlertCircle size={20} />
                                    <span className="text-sm">{error}</span>
                                </div>
                            )}

                            {!oobCode ? (
                                <div className="text-center py-4">
                                    <p className="text-gray-400 mb-6">
                                        The password reset link is invalid or has expired. Please request a new link from the login page.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/login')}
                                        className="btn-secondary w-full"
                                    >
                                        Back to Login
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                            <input
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="input-field pl-12"
                                                placeholder="••••••••"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Confirm New Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                            <input
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="input-field pl-12"
                                                placeholder="••••••••"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-primary w-full flex items-center justify-center gap-2"
                                    >
                                        {loading ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                Reset Password
                                                <ArrowRight size={20} />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
